/**
 * The spend screen's decisions: which window to ask for, and how to read what came
 * back.
 *
 * 🔴 **Every one of these is about not reporting a figure X did not give.** The route
 * hands on exactly what X sent — arrays, and `null` where X holds nothing — so the work
 * of telling "nothing was spent" from "nobody knows" is here, in functions a runner can
 * reach, rather than in a component nothing runs.
 */
import type { StatsAnswer, StatsRow } from "./api.ts";

/**
 * One instant in the only form `GET /api/v1/stats` accepts: a whole hour, UTC.
 *
 * 🔴 **Built by hand rather than with `toISOString().slice(…)`**, because a slice is a
 * silent truncation: a date whose minutes are not zero would come out looking like a
 * whole hour while naming a different instant. Here the minutes and seconds are literal
 * zeros, and the hour is the one asked for.
 */
export function wholeHourUtc(when: Date): string {
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${String(when.getUTCFullYear())}-${pad(when.getUTCMonth() + 1)}-${pad(
		when.getUTCDate(),
	)}T${pad(when.getUTCHours())}:00:00Z`;
}

/** Seven days of whole hours, ending at the top of the current hour. */
export interface StatsWindow {
	start: string;
	end: string;
}

/**
 * The window the screens ask for: the last seven days, to the top of this hour.
 *
 * 🔴 **`end` is exclusive, so the hour in progress is NOT counted** — X states the
 * exclusivity itself, as the reason its figures differ from the Ads UI's, where the end
 * is inclusive. Rounding up to the next hour instead would ask X for a window that
 * reaches into the future, and rounding down is what makes the figure reproducible: two
 * reads within the same hour ask the same question and can be compared.
 *
 * Seven days is X's maximum on this endpoint, not a choice of this console.
 */
export function sevenDayWindow(now: Date): StatsWindow {
	const end = new Date(now);
	end.setUTCMinutes(0, 0, 0);
	const start = new Date(end);
	start.setUTCDate(start.getUTCDate() - 7);
	return { start: wholeHourUtc(start), end: wholeHourUtc(end) };
}

/**
 * One figure out of a `TOTAL` read.
 *
 * 🔴 **Three kinds, because three things can be true and a screen must not say the
 * same of all of them.** `nothing` is X holding nothing for the metric — X returns
 * `null` there, and says an all-null answer likely means the entity did not serve in
 * the window. `value` is a figure. `unreadable` is X answering with a number of buckets
 * that a `TOTAL` read cannot have, which is X disagreeing with the question rather than
 * answering it — and collapsing that into `nothing` would report "no spend" for an
 * answer nobody could read.
 */
export type TotalFigure =
	| { kind: "nothing" }
	| { kind: "value"; micro: number }
	| { kind: "unreadable"; buckets: number };

/**
 * Read the one bucket a `TOTAL` read has.
 *
 * 🔴 It does **not** sum. Under `TOTAL` X sends one entry; a sum over several would
 * quietly answer a question nobody asked (and would be wrong for `DAY`, where the
 * screens do not ask and the sum would hide which day). More than one bucket here is
 * reported as `unreadable`, which is what it is.
 */
export function totalFigure(values: number[] | null | undefined): TotalFigure {
	if (values === null || values === undefined) return { kind: "nothing" };
	if (values.length !== 1) return { kind: "unreadable", buckets: values.length };
	const micro = values[0];
	// A non-number in a documented array of ints is X answering something this console
	// cannot read, not a zero.
	if (typeof micro !== "number" || !Number.isFinite(micro))
		return { kind: "unreadable", buckets: values.length };
	return { kind: "value", micro };
}

/**
 * The row for one entity, out of an answer that may carry several.
 *
 * 🔴 **A missing row is `null`, never the first row.** The route asks for up to twenty
 * ids at once, and X's answer is a list; taking `rows[0]` for a named id is how one
 * campaign's spend ends up printed against another's. X documents the row as present
 * with null metrics when the entity did not serve, so an absent row is a shape X does
 * not document at all — which is exactly when guessing is worst.
 */
export function rowFor(rows: readonly StatsRow[] | null | undefined, id: string): StatsRow | null {
	return rows?.find((row) => row.id === id) ?? null;
}

/**
 * The currency to label a figure with, out of the account's funding instruments.
 *
 * 🔴 **Disagreement answers `null`, and so does a single missing code.** An account can
 * hold several funding instruments, and nothing says they share a currency; labelling
 * micros with the first one found would put a code on a figure that may have been
 * billed in another. `null` makes the screen print the raw micro figure, which is the
 * honest shape of "this console cannot say which currency this is".
 */
export function accountCurrency(
	instruments: readonly { currency: string | null }[] | null | undefined,
): string | null {
	const codes = instruments?.map((i) => i.currency) ?? [];
	if (codes.length === 0) return null;
	const first = codes[0];
	if (first === null || first === "") return null;
	return codes.every((code) => code === first) ? first : null;
}

/** X's ceiling on one synchronous analytics request, from its own parameter table. */
export const STATS_MAX_IDS = 20;

/**
 * The ids a screen may ask about in one request, and whether that left any out.
 *
 * 🔴 **`cut` exists so the screen can SAY it**, which is the whole point: a page can
 * hold up to a thousand rows and X accepts twenty ids per request, so a figure column
 * that silently covered the first twenty would be a correct number against the wrong
 * rows — the rest showing nothing and looking like rows that spent nothing. The ids are
 * taken in the order the rows are on screen, so what is covered is what is at the top.
 */
export function idsForStats(
	ids: readonly string[],
	max: number = STATS_MAX_IDS,
): { ids: string[]; cut: number } {
	return { ids: ids.slice(0, max), cut: Math.max(0, ids.length - max) };
}

/**
 * What one row of the spend column is, before anything words it.
 *
 * 🔴 **Five kinds, and the fifth is why this function exists** (d6, 2026-10-08). The
 * screens used to print `not asked` for every row the answer did not mention, and
 * `!row` is true in two unrelated situations: the id was never in the request, because
 * X answers about at most twenty entities; or the id WAS in the request and X did not
 * mention it. For the second, "not asked" names a mechanism that did not happen — the
 * exact class this whole feature was built to avoid, one level down. And `rowFor`'s own
 * comment already said an absent row is a shape X does not document, "which is exactly
 * when guessing is worst".
 *
 * It is a function rather than one more condition in each screen because the defect had
 * **two sites** and the reviewer found one: he read a screen, wrote "the cell" as though
 * there were a single one, and the second site stayed hidden until it was named. A
 * condition closes the two sites that exist; a function closes the class, and makes the
 * population visible to a case.
 */
export type BilledRowState =
	| { kind: "not-asked" }
	| { kind: "read-failed" }
	| { kind: "unread" }
	| { kind: "absent" }
	| { kind: "figure"; figure: TotalFigure };

/**
 * Decide one row's state.
 *
 * 🔴 **The order is the argument, not a convenience.** `not-asked` is settled FIRST,
 * because it is a property of the **request** — this console knows it built a request
 * without that id, whatever X then answered or failed to answer. Deciding it after the
 * read would make a row outside the twenty report the read's fate (`read failed`,
 * `reading…`) and so claim an attempt that never happened.
 *
 * After that the answer's own fate decides: a failed read says so; a read not yet back
 * says so; an answer that does not mention an id this console DID ask about is `absent`,
 * which is X's silence and not a zero.
 */
export function billedRowState(args: {
	id: string;
	asked: readonly string[];
	failed: boolean;
	answer: StatsAnswer | null;
}): BilledRowState {
	return metricRowState(args, "billedChargeLocalMicro");
}

/**
 * The five figures one row of the answer carries — the route's own five, no more
 * (three until 2026-10-21; `urlClicks` and `clicks` arrived with twads#174). What a screen
 * SHOWS is its own list: the campaign card shows four (`METRICS` in campaign-card-copy.ts,
 * `clicks` read and not shown), the two tables Spend and URL clicks.
 */
export type StatsMetric =
	| "billedChargeLocalMicro"
	| "impressions"
	| "engagements"
	| "urlClicks"
	| "clicks";

/**
 * The same decision for any of the five metrics (the campaign card shows four of them,
 * the tables two). The five states are properties of the request and the answer, not of
 * the metric, so they are decided once here and `billedRowState` is this with the
 * billed figure named. Only the last line reads the metric.
 */
export function metricRowState(
	args: {
		id: string;
		asked: readonly string[];
		failed: boolean;
		answer: StatsAnswer | null;
	},
	metric: StatsMetric,
): BilledRowState {
	if (!args.asked.includes(args.id)) return { kind: "not-asked" };
	if (args.failed) return { kind: "read-failed" };
	if (args.answer === null) return { kind: "unread" };
	const row = rowFor(args.answer.rows, args.id);
	if (row === null) return { kind: "absent" };
	return { kind: "figure", figure: totalFigure(row[metric]) };
}
